import {
  Box,
  ClickAwayListener,
  createTheme,
  TextField,
  ThemeProvider
} from '@mui/material'
import { StaticDatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateRangePicker } from 'components/calendar/ViewDatesPicker'
import Switch from 'components/form/Switch'
import { PopupInstance } from 'components/popup/Popup'
import { PopupSectionRow } from 'components/popup/PopupSectionRow'
import dayjs, { Dayjs } from 'dayjs'
import { IEventDetails } from 'hooks/useEventDetails'
import i18n from 'i18n'
import { t } from 'i18next'
import { getDefaultTimezone } from 'index'
import { ChangeEvent, ReactNode, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { Controller, ControllerRenderProps } from 'react-hook-form'
import Logger from 'services/Logger'
import { DocType, EventDoc } from 'types/api/changes'
import { APP_COLOR } from 'utils/colors'

import * as Styled from '../styled'

interface ContentProps {
  open: boolean
  setOpen: (b: boolean) => void
  instance: PopupInstance | null | undefined
  value: Dayjs | null
  onAccept: (date: Dayjs | null) => void
  eventHook: IEventDetails
  instanceStart: number
}
const theme = createTheme({
  palette: {
    input: {
      border: {
        primary: undefined
      },
      placeholder: undefined
    },
    border: {
      primary: undefined
    },
    primary: {
      main: String(APP_COLOR)
    },
    card: {
      primary: undefined
    },
    button: {
      background: {
        primary: undefined,
        selected: undefined
      },
      text: {
        primary: undefined,
        approve: undefined,
        tenative: undefined,
        negative: undefined,
        groupcal: undefined,
        link: undefined,
        darkRed: undefined
      }
    }
  },
  components: {
    MuiIconButton: {
      styleOverrides: {
        sizeMedium: {
          color: String(APP_COLOR)
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: String(APP_COLOR)
        }
      }
    }
  }
})

function Content(props: ContentProps) {
  const [currentValue, setCurrentValue] = useState(props.value)

  const handleClickAway = () => {
    props.setOpen(false)
  }

  const toggleState = () => {
    const newState = props.eventHook.getValues('someday') === '1' ? '0' : '1'

    props.eventHook.setSomeday(newState)

    if (newState === '1') {
      props.setOpen(false)
    }
  }

  Logger.blue('currentValue', currentValue)

  const onSwitchChange =
    (field?: ControllerRenderProps<EventDoc, any>) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      toggleState()
    }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={handleClickAway}
    >
      <div style={{ color: 'black' }}>
        <Controller
          name="someday"
          control={props.eventHook.control}
          defaultValue={props.eventHook.someday}
          render={({ field }) => {
            console.log(props.eventHook.timeZone)
            return (
              <Box>
                {props.eventHook.getValues('someday') != '1' && (
                  <DateRangePicker
                    mode={'single'}
                    defaultRange={{
                      from: currentValue?.toDate(),
                      to: currentValue?.toDate()
                    }}
                    defaultDate={currentValue?.toDate()}
                    onSelect={(selected: Date | DateRange | undefined) => {
                      console.log(selected)
                      if (selected instanceof Date) {
                        const currentHour = props.value?.hour() ?? 0
                        const currentMinute = props.value?.minute() ?? 0

                        // Extract the date components (year, month, day) from the selected date
                        const selectedYear = selected.getFullYear()
                        const selectedMonth = selected.getMonth()
                        const selectedDate = selected.getDate()

                        // Create a new date in the target timezone while preserving the selected date
                        const newValue = dayjs()
                          .tz(props.eventHook.timeZone)
                          .year(selectedYear)
                          .month(selectedMonth)
                          .date(selectedDate)
                          .hour(
                            props.eventHook.getValues('AllDay') === '1'
                              ? 13
                              : currentHour
                          )
                          .minute(
                            props.eventHook.getValues('AllDay') === '1'
                              ? 0
                              : currentMinute
                          )
                          .second(0)

                        Logger.blue('Picked date', newValue)

                        setCurrentValue(newValue)
                        props.instance?.hide()
                        props.onAccept(newValue)
                      }
                    }}
                  />
                )}
                {props.eventHook.getValues('Type') === DocType.TASK && (
                  <Box
                    margin={theme.spacing(0, 3)}
                    minWidth={'200px'}
                    sx={{ direction: i18n.dir() }}
                  >
                    <PopupSectionRow
                      title={t('somedayTaskTitle')}
                      action={toggleState}
                      value={
                        <Switch
                          id="someday-switch"
                          onChange={() => {
                            // do nothing, row handles it
                          }}
                          checked={props.eventHook.getValues('someday') === '1'}
                        />
                      }
                    />
                  </Box>
                )}
              </Box>
            )
          }}
        />
      </div>
    </ClickAwayListener>
  )
}

interface DatePickPopupProps {
  open: boolean
  setOpen: (b: boolean) => void
  children: ReactNode
  value: Dayjs | null
  onAccept: (date: Dayjs | null) => void
  eventHook: IEventDetails
  instanceStart: number
}

export default function DatePickPopup(props: DatePickPopupProps) {
  const [instance, onInstance] = useState<PopupInstance>()
  return (
    <Styled.DatePickPopup
      width="300px"
      open={props.open}
      onMount={onInstance}
      content={<Content {...props} instance={instance} />}
      placement={'left'}
    >
      {props.children}
    </Styled.DatePickPopup>
  )
}
